/**
 * @description Super Admin Channel Partner detail (LYN-288 / LYN-158 / LYN-157 / LYN-323 / LYN-325)
 *
 * Key features:
 * - Overview: Admin User invite banner, then name, slug, Host + Admin Host, Wristband tenant
 * - Sales Orgs: shared list chrome (Tenant Name, Owner, Status, Merchants, Created)
 *   + SA Create (LYN-323). Row click opens existing `/tenants/{id}`.
 * - Merchants: shared list chrome (Business, Status, Users, Created) — no Hosting
 * - Admins: Super Admin start / stop switch-into (LYN-157). Names stay Switch-into only.
 * - Configurations: Signup capabilities (320) only
 * - Domains: Primary vanity (340) + Sending domain (308)
 * - Branding: full editor (LYN-309) — upload / modify logo, app icon, favicon
 *
 * @dependencies
 * - adminApi: getChannelPartner()
 * - useOverlay: start / stop on /api/switch
 * - getChannelPartnerHostPreview: current-env app host only
 * - getChannelPartnerAdminHostPreview: API adminHost (invite-mint helper, LYN-337)
 * - AdminListTable + cpSalesOrgColumns / cpMerchantColumns
 * - ChannelPartnerEmailSendingDomainPanel: 161 sheet (later CP admin remount)
 *
 * @notes
 * - Configurations is flags only. Signup capabilities (320).
 *   Payment Gateways / CMS later. Email sheet lives on Domains.
 * - No feature packs / cp_features
 * - No attach-existing LynxPay SO control
 * - SA Create Sales Org is LYN-323 (new form, not the LynxPay wizard)
 * - Sales Orgs row click-into is LYN-325 — reuse TenantDetail by salesOrgId
 * - No name+slug stack and no nested merchant names on the Sales Orgs list
 * - No Invite on the Sales Orgs row. Admins tab still lists merchants for Switch-into
 * - Overview host is current-env only — never both prod and staging strings
 * - Stay on admin.lynx.build. Overlay does not bounce to a CP host.
 * - Parent id for Create is the URL :id, not the overlay
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import {
  ArrowLeft,
  AlertCircle,
  Building2,
  Check,
  Globe,
  Handshake,
  Loader2,
  Palette,
  Send,
  SlidersHorizontal,
  Store,
  Users,
} from 'lucide-react';
import {
  getChannelPartner,
  sendChannelPartnerInvite,
  updateChannelPartnerBranding,
  uploadChannelPartnerLogo,
  uploadChannelPartnerLogoMark,
  uploadChannelPartnerFavicon,
  deleteChannelPartnerLogo,
  deleteChannelPartnerLogoMark,
  deleteChannelPartnerFavicon,
  updateChannelPartnerSignupCapabilities,
  getChannelPartnerSourceConnectors,
  saveChannelPartnerSourceConnector,
  uploadChannelPartnerSourceConnectorLogo,
  getChannelPartnerSourceConnectorFlags,
  saveChannelPartnerSourceConnectorFlag
} from '../../services/api/adminApi';
import SourceConnectorPanel from '../../components/source-connectors/SourceConnectorPanel';
import { createLatestLoadGate } from '../../components/source-connectors/latestLoadGate';
import ChannelPartnerBrandingEditor from '../../components/channel-partner/ChannelPartnerBrandingEditor';
import ChannelPartnerEmailSendingDomainPanel from '../../components/channel-partners/ChannelPartnerEmailSendingDomainPanel';
import ChannelPartnerPrimaryDomainPanel from '../../components/channel-partners/ChannelPartnerPrimaryDomainPanel';
import { getChannelPartnerAdminHostPreview, getChannelPartnerHostPreview } from '../../config/domains';
import { useOverlay } from '../../contexts/OverlayContext';
import AdminListTable from '../../components/admin-list/AdminListTable';
import { cpMerchantColumns, cpSalesOrgColumns } from '../../components/admin-list/adminListColumns';
import {
  CP_DETAIL_TAB_QUERY,
  createSalesOrgUnderCpPath,
  salesOrgsTabIndexFromSearch,
  tenantDetailPath
} from '../../utils/createSalesOrgUnderCp';

export const TABS = [
  { name: 'Overview', icon: Handshake },
  { name: 'Sales Orgs', icon: Building2 },
  { name: 'Merchants', icon: Store },
  { name: 'Configurations', icon: SlidersHorizontal },
  { name: 'Domains', icon: Globe },
  { name: 'Branding', icon: Palette },
  { name: 'Admins', icon: Users },
];

function ChannelPartnerBrandingTab({ partner, onUpdate }) {
  const handleSave = async (payload) => {
    await updateChannelPartnerBranding(partner.id, payload);
    await onUpdate();
  };

  const handleUpload = async (kind, file) => {
    if (kind === 'logo') await uploadChannelPartnerLogo(partner.id, file);
    else if (kind === 'logoMark') await uploadChannelPartnerLogoMark(partner.id, file);
    else await uploadChannelPartnerFavicon(partner.id, file);
    await onUpdate();
  };

  const handleDelete = async (kind) => {
    if (kind === 'logo') await deleteChannelPartnerLogo(partner.id);
    else if (kind === 'logoMark') await deleteChannelPartnerLogoMark(partner.id);
    else await deleteChannelPartnerFavicon(partner.id);
    await onUpdate();
  };

  return (
    <ChannelPartnerBrandingEditor
      branding={partner.branding || {}}
      onSave={handleSave}
      onUpload={handleUpload}
      onDelete={handleDelete}
    />
  );
}

function OverviewTab({ partner, onUpdate }) {
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const invitationStatus = partner.admin?.invitationStatus || 'not_invited';

  const handleSendInvite = async () => {
    setSendingInvite(true);
    setInviteResult(null);
    try {
      const result = await sendChannelPartnerInvite(partner.id);
      setInviteResult({ type: 'success', message: result.message || 'Invitation sent' });
      await onUpdate();
      setTimeout(() => setInviteResult(null), 5000);
    } catch (err) {
      setInviteResult({
        type: 'error',
        message: err.response?.data?.error?.message || 'Failed to send invite'
      });
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <div className="space-y-6">
      {partner.admin && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Admin User</p>
          </div>
          <div className="px-4 py-3 space-y-3">
            <Row label="Email" value={partner.admin.email} />
            <Row
              label="Status"
              value={
                invitationStatus === 'accepted' ? 'Accepted' :
                invitationStatus === 'pending' ? 'Invite Sent' :
                invitationStatus === 'expired' ? 'Invite Expired' :
                'Awaiting Invite'
              }
            />
            {invitationStatus !== 'accepted' && (
              <div className="px-5 pb-3">
                {inviteResult && (
                  <div className={`mb-2 p-2 rounded text-xs flex items-center gap-1.5 ${
                    inviteResult.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {inviteResult.type === 'success' ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    {inviteResult.message}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleSendInvite}
                  disabled={sendingInvite}
                  className="inline-flex items-center gap-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded-md font-medium disabled:opacity-50 transition-colors"
                >
                  {sendingInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {invitationStatus === 'pending' || invitationStatus === 'expired'
                    ? 'Resend Invite'
                    : 'Send Invite'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        <div className="px-5 py-3">
          <p className="text-sm font-medium text-gray-900">Organization Details</p>
        </div>
        <Row label="Partner name" value={partner.name} />
        <Row label="Slug" value={partner.slug} mono />
        <Row label="Host" value={getChannelPartnerHostPreview(partner.slug)} mono />
        <Row label="Admin Host" value={getChannelPartnerAdminHostPreview(partner)} mono />
        <Row label="Wristband tenant" value={partner.wristbandTenantId} mono />
        <Row label="Wristband domain" value={partner.wristbandTenantDomain} mono />
        <Row label="Admin User" value={
          partner.admin
            ? `${partner.admin.firstName || ''} ${partner.admin.lastName || ''} · ${partner.admin.email || ''}`.trim()
            : '—'
        } />
        <Row label="Lynx role" value={partner.admin?.role || 'channel_partner'} mono />
        <Row label="Status" value={partner.isActive ? 'Active' : 'Inactive'} />
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-3 gap-4 px-5 py-3">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`col-span-2 text-sm text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );
}

function SalesOrgsTab({ channelPartnerId, salesOrganizations }) {
  const rows = salesOrganizations || [];
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-medium text-gray-900">Sales Orgs</h2>
          <p className="text-xs text-gray-500 mt-0.5">{rows.length} total</p>
        </div>
        <button
          type="button"
          onClick={() => navigate(createSalesOrgUnderCpPath(channelPartnerId))}
          className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-500"
        >
          Create
        </button>
      </div>
      <AdminListTable
        columns={cpSalesOrgColumns()}
        rows={rows}
        rowKey={(so) => so.id}
        onRowClick={(so) => navigate(tenantDetailPath(so.id))}
        emptyIcon={Building2}
        emptyMessage="No sales organizations under this partner yet."
      />
    </div>
  );
}

function SignupToggle({ label, description, enabled, busy, onToggle }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        disabled={busy}
        onClick={() => onToggle(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50 ${
          enabled ? 'bg-indigo-600' : 'bg-gray-200'
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );
}

function ConfigurationsTab({ channelPartnerId, partner, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const directOn = partner?.directApplicationSignupEnabled !== false;
  const soOn = partner?.salesOrgApplicationSignupEnabled !== false;

  const handleToggle = async (fields) => {
    setBusy(true);
    setError(null);
    try {
      await updateChannelPartnerSignupCapabilities(channelPartnerId, fields);
      await onUpdate();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to update signup capabilities');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-900">Signup capabilities</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Public signup is the merchant form on this partner&apos;s app and on that
            partner&apos;s Sales Org apps. Turning Sales Org applications off hides
            those orgs&apos; switch and treats signup as off without changing their last choice.
          </p>
        </div>
        <div className="px-5 divide-y divide-gray-100">
          <SignupToggle
            label="Direct application"
            description="Merchants may sign up on this Channel Partner app."
            enabled={directOn}
            busy={busy}
            onToggle={(next) => handleToggle({ directApplicationSignupEnabled: next })}
          />
          <SignupToggle
            label="Sales Org applications"
            description="Sales Orgs under this partner may offer public merchant signup on their own apps."
            enabled={soOn}
            busy={busy}
            onToggle={(next) => handleToggle({ salesOrgApplicationSignupEnabled: next })}
          />
        </div>
        {error && (
          <p className="px-5 pb-3 text-xs text-red-600">{error}</p>
        )}
      </section>
      <section className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-900">Flags</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Email this ticket. Payment Gateways, CMS, and Audit Logs flags ship later.
          </p>
        </div>
        <div className="px-5 py-3">
          <p className="text-sm font-medium text-gray-900">Email</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Sending-domain DNS lives on Domains. Factory Integrations → Email stays Lynx-wide.
          </p>
        </div>
      </section>
      <ChannelPartnerConnectorsSection channelPartnerId={channelPartnerId} />
    </div>
  );
}

function ChannelPartnerConnectorsSection({ channelPartnerId }) {
  const [connectors, setConnectors] = useState(null);
  const [salesOrganizations, setSalesOrganizations] = useState([]);
  const [flagsByConnectorId, setFlagsByConnectorId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const loadGate = useRef(createLatestLoadGate());

  const load = useCallback(async () => {
    const isCurrent = loadGate.current.begin();
    let list;
    try {
      const res = await getChannelPartnerSourceConnectors(channelPartnerId);
      if (!isCurrent()) return;
      list = res.data || [];
      setError(null);
      setConnectors(list);
      setSalesOrganizations(res.meta?.salesOrganizations || []);
      setFlagsByConnectorId(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err.response?.data?.error?.message || 'Failed to load connectors');
      setConnectors([]);
      return;
    }
    try {
      const flags = {};
      await Promise.all(list.map(async (connector) => {
        const flagRes = await getChannelPartnerSourceConnectorFlags(channelPartnerId, connector.id);
        flags[connector.id] = flagRes.data || [];
      }));
      if (!isCurrent()) return;
      setFlagsByConnectorId(flags);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err.response?.data?.error?.message || 'Failed to load connectors');
    }
  }, [channelPartnerId]);

  useEffect(() => {
    const gate = loadGate.current;
    load();
    return () => {
      gate.invalidate();
    };
  }, [load]);

  return (
    <SourceConnectorPanel
      connectors={connectors}
      salesOrganizations={salesOrganizations}
      flagsByConnectorId={flagsByConnectorId}
      busy={busy}
      error={error}
      onSaveCatalog={async (connectorId, body) => {
        setBusy(true);
        setError(null);
        try {
          await saveChannelPartnerSourceConnector(channelPartnerId, connectorId, body);
          await load();
        } catch (err) {
          setError(err.response?.data?.error?.message || 'Failed to save connector');
        } finally {
          setBusy(false);
        }
      }}
      onSaveFlag={async (connectorId, body) => {
        if (flagsByConnectorId == null) return;
        setBusy(true);
        setError(null);
        try {
          await saveChannelPartnerSourceConnectorFlag(channelPartnerId, connectorId, body);
          await load();
        } catch (err) {
          setError(err.response?.data?.error?.message || 'Failed to save flag');
        } finally {
          setBusy(false);
        }
      }}
      onUploadLogo={async (connectorId, file) => {
        setBusy(true);
        setError(null);
        try {
          await uploadChannelPartnerSourceConnectorLogo(channelPartnerId, connectorId, file);
          await load();
        } catch (err) {
          setError(err.response?.data?.error?.message || 'Failed to upload logo');
        } finally {
          setBusy(false);
        }
      }}
    />
  );
}

function DomainsTab({ channelPartnerId }) {
  return (
    <div className="space-y-6">
      <ChannelPartnerPrimaryDomainPanel channelPartnerId={channelPartnerId} />
      <ChannelPartnerEmailSendingDomainPanel channelPartnerId={channelPartnerId} />
    </div>
  );
}

function MerchantsTab({ directMerchants }) {
  const rows = directMerchants || [];

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-sm font-medium text-gray-900">Merchants</h2>
        <p className="text-xs text-gray-500 mt-0.5">{rows.length} total</p>
      </div>
      <AdminListTable
        columns={cpMerchantColumns()}
        rows={rows}
        rowKey={(merchant) => merchant.id}
        emptyIcon={Store}
        emptyMessage="No direct merchants under this partner yet."
      />
    </div>
  );
}

function SwitchButton({ kind, id, label, overlay, busy, onStart }) {
  const active = overlay
    && ((kind === 'channel_partner' && overlay.channelPartnerId === id && overlay.kind === 'channel_partner')
      || (kind === 'sales_org' && overlay.salesOrgId === id && overlay.kind === 'sales_org')
      || (kind === 'merchant' && overlay.companyId === id));

  return (
    <button
      type="button"
      onClick={() => onStart(kind, id)}
      disabled={busy || active}
      className={`text-xs font-medium px-2.5 py-1 rounded-md border ${
        active
          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
      } disabled:opacity-50`}
    >
      {active ? 'Current overlay' : `Switch into ${label}`}
    </button>
  );
}

function AdminsTab({ partner, overlay, busy, onStart, onStop }) {
  const salesOrganizations = partner.organizations?.salesOrganizations || [];
  const directMerchants = partner.organizations?.directMerchants || [];

  return (
    <div className="space-y-4">
      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-medium text-gray-900">Switch-into</h2>
        <p className="text-xs text-gray-500 mt-1">
          Stay Super Admin on this site. Overlay tags the Channel Partner tree — it does not log in as them.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <SwitchButton
            kind="channel_partner"
            id={partner.id}
            label={partner.name}
            overlay={overlay}
            busy={busy}
            onStart={onStart}
          />
          {overlay && (
            <button
              type="button"
              onClick={onStop}
              disabled={busy}
              className="text-xs font-medium px-2.5 py-1 rounded-md bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-50"
            >
              Exit overlay
            </button>
          )}
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-medium text-gray-900">Sales Orgs</h2>
        {salesOrganizations.length === 0 ? (
          <p className="text-sm text-gray-400 mt-3">No sales organizations under this partner yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100">
            {salesOrganizations.map((so) => (
              <li key={so.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{so.organizationName}</p>
                    <p className="text-xs text-gray-400 font-mono">{so.slug}</p>
                  </div>
                  <SwitchButton
                    kind="sales_org"
                    id={so.id}
                    label={so.organizationName}
                    overlay={overlay}
                    busy={busy}
                    onStart={onStart}
                  />
                </div>
                {(so.merchants || []).map((merchant) => (
                  <div key={merchant.id} className="mt-2 pl-4 flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-600">{merchant.businessName}</p>
                    <SwitchButton
                      kind="merchant"
                      id={merchant.id}
                      label={merchant.businessName}
                      overlay={overlay}
                      busy={busy}
                      onStart={onStart}
                    />
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-medium text-gray-900">CP-direct merchants</h2>
        {directMerchants.length === 0 ? (
          <p className="text-sm text-gray-400 mt-3">No direct merchants under this partner yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100">
            {directMerchants.map((merchant) => (
              <li key={merchant.id} className="py-3 flex items-center justify-between gap-3">
                <p className="text-sm text-gray-900">{merchant.businessName}</p>
                <SwitchButton
                  kind="merchant"
                  id={merchant.id}
                  label={merchant.businessName}
                  overlay={overlay}
                  busy={busy}
                  onStart={onStart}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ChannelPartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { overlay, busy, start, stop } = useOverlay();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const selectedIndex = salesOrgsTabIndexFromSearch(searchParams);

  const fetchPartner = useCallback(async () => {
    try {
      const result = await getChannelPartner(id);
      if (result.success) {
        setPartner(result.data);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load channel partner');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load channel partner</p>
        <p className="text-gray-500 text-sm">{error || 'Channel partner not found'}</p>
        <button
          onClick={() => navigate('/channel-partners')}
          className="mt-4 text-sm text-indigo-600 hover:text-indigo-500"
        >
          Back to Channel Partners
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/channel-partners')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Channel Partners
      </button>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">{partner.name}</h1>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          partner.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {partner.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <Tab.Group
        selectedIndex={selectedIndex}
        onChange={(index) => {
          const tab = Object.keys(CP_DETAIL_TAB_QUERY).find(
            (key) => CP_DETAIL_TAB_QUERY[key] === index
          );
          if (!tab || tab === 'overview') {
            setSearchParams({}, { replace: true });
            return;
          }
          setSearchParams({ tab }, { replace: true });
        }}
      >
        <Tab.List className="flex border-b border-gray-200 mb-6">
          {TABS.map((tab) => (
            <Tab key={tab.name} as={Fragment}>
              {({ selected }) => (
                <button
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none ${
                    selected
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                </button>
              )}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <OverviewTab partner={partner} onUpdate={fetchPartner} />
          </Tab.Panel>
          <Tab.Panel>
            <SalesOrgsTab
              channelPartnerId={id}
              salesOrganizations={partner.organizations?.salesOrganizations}
            />
          </Tab.Panel>
          <Tab.Panel>
            <MerchantsTab directMerchants={partner.organizations?.directMerchants} />
          </Tab.Panel>
          <Tab.Panel>
            <ConfigurationsTab
              channelPartnerId={id}
              partner={partner}
              onUpdate={fetchPartner}
            />
          </Tab.Panel>
          <Tab.Panel>
            <DomainsTab channelPartnerId={id} />
          </Tab.Panel>
          <Tab.Panel>
            <ChannelPartnerBrandingTab partner={partner} onUpdate={fetchPartner} />
          </Tab.Panel>
          <Tab.Panel>
            <AdminsTab
              partner={partner}
              overlay={overlay}
              busy={busy}
              onStart={start}
              onStop={stop}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}

export default ChannelPartnerDetail;
